<template>
  <div class="bj-unit-schedule-tour">
    <div
      v-if="isLoadingSlots"
      class="text-center q-py-lg"
    >
      <q-spinner-puff
        color="primary"
        size="52px"
      />
    </div>

    <div v-else>
      <div
        v-if="!dateOptions.length"
        class="text-grey-7 q-mb-sm"
      >
        No showing slots are available.
      </div>

      <template v-else>
        <div class="slot-section q-mb-md">
          <div class="slot-section-header q-mb-sm">
            <div class="slot-section-label text-grey-7">
              Day
            </div>
            <div
              v-if="chosenDateOption"
              class="slot-section-chosen"
            >
              {{ chosenDateSummary }}
            </div>
          </div>

          <div class="row gutter-sm slot-grid">
            <div
              v-for="dateOption in dateOptions"
              :key="dateOption.date"
              class="col-2 q-mb-sm"
            >
              <button
                type="button"
                class="slot-card date-option"
                :class="{
                  chosen: chosenDateOption && chosenDateOption.date === dateOption.date,
                }"
                :disabled="dateOption.disabled"
                @click="chooseDate(dateOption)"
              >
                <div class="month">
                  {{ dateOption.month }}
                </div>
                <div class="day-row">
                  <div class="day-of-month">
                    {{ dateOption.dayOfMonth }}
                  </div>
                  <div class="day-of-month-ordinal">
                    {{ dateOption.dayOfMonthOrdinal }}
                  </div>
                </div>
                <div class="day-of-week">
                  {{ dateOption.dayOfWeek }}
                </div>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="chosenDateOption"
          class="slot-section"
        >
          <div class="slot-section-header q-mb-sm">
            <div class="slot-section-label text-grey-7">
              Time
            </div>
            <div
              v-if="chosenTimeOption"
              class="slot-section-chosen"
            >
              {{ chosenTimeOption.time }}
            </div>
          </div>

          <div class="q-mb-sm flex justify-center mb-4">
            <q-btn-group outline>
              <q-btn
                size="sm"
                :disable="isAvailableTimesButtonDisabled"
                :color="showAllSlots ? 'grey-7' : 'primary'"
                :outline="showAllSlots"
                label="Available times"
                @click="showAllSlots = false"
              />
              <q-btn
                size="sm"
                :color="showAllSlots ? 'primary' : 'grey-7'"
                :outline="!showAllSlots"
                label="All times"
                @click="showAllSlots = true"
              >
                <q-tooltip
                  anchor="top middle"
                  self="bottom middle"
                >
                  Also show blocked and constrained times that you can override
                </q-tooltip>
              </q-btn>
            </q-btn-group>
          </div>

          <div
            v-if="isLoadingDateSlots"
            class="text-center q-py-md"
          >
            <q-spinner-puff
              color="primary"
              size="32px"
            />
          </div>

          <div
            v-else
            class="row gutter-sm slot-grid slot-grid-time"
          >
            <div
              v-for="timeOption in timeOptions"
              :key="timeOption.datetime"
              class="col-2 q-mb-sm"
            >
              <button
                type="button"
                class="slot-card time-option"
                :class="{
                  chosen: chosenTimeOption && chosenTimeOption.datetime === timeOption.datetime,
                  overridable: timeOption.slot_state === 'overridable',
                  blocked: timeOption.slot_state === 'blocked',
                }"
                :disabled="timeOption.disabled"
                @click="chooseTime(timeOption)"
              >
                <div class="time">
                  {{ timeOption.time }}
                  <span
                    v-if="timeOption.tooltip && timeOption.tooltip.length"
                    class="tooltip-indicator"
                  >
                    <q-icon
                      name="info"
                      size="14px"
                    />
                    <q-tooltip
                      anchor="top middle"
                      self="bottom middle"
                      :delay="450"
                    >
                      <div
                        v-for="(line, index) in timeOption.tooltip"
                        :key="`tooltip-${timeOption.datetime}-${index}`"
                        class="q-mb-xs"
                      >
                        {{ line }}
                      </div>
                    </q-tooltip>
                  </span>
                </div>
              </button>
            </div>
            <div
              v-if="!timeOptions.length"
              class="col-12 text-grey-7"
            >
              No times are available for the selected day.
            </div>
          </div>
        </div>
      </template>

      <div
        v-if="warningsRef.length || restrictionWarningsRef.length || assigneeMismatchWarningDataRef"
        class="q-mt-sm"
      >
        <p class="text-weight-medium q-mb-xs">
          Warnings
        </p>
        <p
          v-for="(warning, index) in warningsRef"
          :key="`warning-${index}`"
          class="q-mb-xs"
        >
          {{ warning }}
        </p>
        <ul
          v-if="restrictionWarningsRef.length || assigneeMismatchWarningDataRef"
          class="q-pl-md q-mb-none"
        >
          <li
            v-if="assigneeMismatchWarningDataRef"
            class="q-mb-xs"
          >
            There are already
            <strong>{{ assigneeMismatchWarningDataRef.leadCount }}</strong>
            prospects scheduled to tour the property at that time with
            <strong>{{ assigneeMismatchWarningDataRef.slotAssigneeName }}</strong>.
            The new prospect will be added and the tour will be re-assigned to
            <strong>{{ assigneeMismatchWarningDataRef.chosenAssigneeLabel }}</strong>.
          </li>
          <li
            v-for="(line, index) in restrictionWarningsRef"
            :key="`restriction-${index}`"
            class="q-mb-xs"
          >
            {{ line }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash.get';

export default {
  name: 'BjUnitScheduleTour',
  props: {
    unit: {
      type: Object,
      required: false,
      default: null,
    },
    isVideo: {
      type: Boolean,
      default: false,
    },
    assigneeId: {
      type: [Number, String],
      default: null,
    },
    chosenAssigneeLabel: {
      type: String,
      default: '',
    },
    disabledDatetime: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isLoadingSlots: false,
      isLoadingDateSlots: false,
      showAllSlots: false,
      showingSlots: {
        slots: [],
        video_slots: [],
      },
      chosenDate: null,
      chosenTimeValue: null,
      selectedTimeOptionRef: null,
      warningsRef: [],
      restrictionWarningsRef: [],
      assigneeMismatchWarningDataRef: null,
    };
  },
  computed: {
    unitId() {
      return get(this.unit, 'id', null);
    },
    timezone() {
      return get(this.unit, 'timezone')
        || get(this.unit, 'property.timezone')
        || get(this.unit, 'property_timezone')
        || this.$momentUtil.tz.guess();
    },
    slotsKey() {
      return this.isVideo ? 'video_slots' : 'slots';
    },
    activeSlots() {
      return this.showingSlots[this.slotsKey] || [];
    },
    dateOptions() {
      return this.activeSlots.map((slot) => this.decorateDateOption(slot));
    },
    chosenDateOption() {
      if (!this.chosenDate) {
        return null;
      }
      return this.dateOptions.find((slot) => slot.date === this.chosenDate) || null;
    },
    chosenDateSummary() {
      if (!this.chosenDateOption) {
        return null;
      }
      return `${this.chosenDateOption.month} ${this.chosenDateOption.dayOfMonth}${this.chosenDateOption.dayOfMonthOrdinal} - ${String(this.chosenDateOption.dayOfWeek || '').toLowerCase()}`;
    },
    chosenTimeOption() {
      if (!this.chosenTimeValue) {
        return null;
      }
      const timeSlots = get(this.chosenDateOption, 'time_slots', null);
      if (!Array.isArray(timeSlots)) {
        return null;
      }
      const timeSlot = timeSlots.find((slot) => slot.value === this.chosenTimeValue || slot.datetime === this.chosenTimeValue);
      return timeSlot ? this.decorateTimeOption(timeSlot) : null;
    },
    isAvailableTimesButtonDisabled() {
      const timeSlots = get(this.chosenDateOption, 'time_slots', null);
      if (this.isLoadingDateSlots || !Array.isArray(timeSlots) || !timeSlots.length) {
        return true;
      }
      return !this.filterAvailableTimeSlots(timeSlots).length;
    },
    timeOptions() {
      const timeSlots = get(this.chosenDateOption, 'time_slots', null);
      if (!Array.isArray(timeSlots)) {
        return [];
      }

      if (this.showAllSlots) {
        return timeSlots.map((slot) => this.decorateTimeOption(slot));
      }

      const filtered = timeSlots.filter((slot) => {
        const state = slot.slot_state || slot.slotState || (slot.disabled ? 'blocked' : 'available');
        return (!slot.disabled && state === 'available') || this.isCurrentTourSlot(slot);
      });

      return filtered.map((slot) => this.decorateTimeOption(slot));
    },
  },
  watch: {
    unitId: {
      immediate: true,
      handler() {
        this.reloadSlots();
      },
    },
    isVideo() {
      this.reloadSlots();
    },
    assigneeId() {
      this.reloadSlots();
    },
    disabledDatetime() {
      this.rebuildCurrentSlots();
      if (this.chosenTimeValue) {
        const selected = this.findSelectedTimeOption(this.chosenTimeValue);
        if (!selected || selected.disabled) {
          this.clearSelectionAndEmit();
        }
      }
    },
  },
  methods: {
    async reloadSlots() {
      this.resetPicker();
      if (!this.unitId) {
        this.showingSlots.slots = [];
        this.showingSlots.video_slots = [];
        return;
      }

      this.isLoadingSlots = true;
      try {
        const response = await this.api().request('GET', this.buildShowingSlotsUrl());
        this.showingSlots.slots = this.normalizeSlotCollection(response.slots, false);
        this.showingSlots.video_slots = this.normalizeSlotCollection(response.video_slots, false);
      } catch (error) {
        this.showingSlots.slots = [];
        this.showingSlots.video_slots = [];
        this.uiNotify(this.getFirstError(error) || error.message || 'Failed to fetch showing slots', 'negative');
      } finally {
        this.isLoadingSlots = false;
      }
    },
    buildShowingSlotsUrl(date = null) {
      const query = [];
      if (date) {
        query.push(`date=${encodeURIComponent(date)}`);
      }
      query.push(`is_video=${this.isVideo ? 'true' : 'false'}`);
      if (this.assigneeId !== null && this.assigneeId !== undefined && `${this.assigneeId}` !== '') {
        query.push(`assignee_id=${encodeURIComponent(this.assigneeId)}`);
      }
      return `units/${this.unitId}/showing-slots${query.length ? `?${query.join('&')}` : ''}`;
    },
    normalizeSlotCollection(slots, timeSlotsLoaded = true) {
      if (!Array.isArray(slots)) {
        return [];
      }
      return slots.map((slot) => this.mapShowingSlot(slot, timeSlotsLoaded));
    },
    mapShowingSlot(slot, timeSlotsLoaded = true) {
      const shouldUseTimeSlots = timeSlotsLoaded && Array.isArray(slot.time_slots);
      const timeSlots = shouldUseTimeSlots
        ? slot.time_slots.map((timeSlot) => this.mapTimeSlot(timeSlot)).filter(Boolean)
        : null;

      return {
        date: slot.date,
        label: this.$momentUtil(slot.date, null).toDateFormat(),
        is_available: this.firstDefined([slot.is_available, slot.isAvailable], true),
        slot_state: this.deriveDateSlotState(slot, timeSlots),
        time_slots_loaded: shouldUseTimeSlots,
        time_slots: timeSlots,
      };
    },
    mapTimeSlot(raw) {
      if (!raw) {
        return null;
      }

      const datetime = raw.tour_at || raw.datetime || raw.date;
      if (!datetime) {
        return null;
      }

      const normalizedDatetime = this.normalizeDatetime(datetime);
      const violations = Array.isArray(raw.violated_restrictions) ? raw.violated_restrictions : [];
      const isViolatingRestrictions = this.firstDefined(
        [raw.is_violating_restrictions, raw.isViolatingRestrictions],
        null,
      );
      const isOverridable = this.firstDefined(
        [raw.is_overrideable, raw.isOverrideable, raw.is_overridable, raw.isOverridable],
        null,
      );
      const slotState = raw.slot_state
        || raw.slotState
        || this.deriveSlotState({
          violations,
          isViolatingRestrictions,
          isOverridable,
          isAvailable: this.firstDefined([raw.is_available, raw.isAvailable], null),
        });
      const tooltip = this.buildTooltip(violations, raw);
      const warnings = this.buildOverridableWarnings(violations, raw);
      const disabledByCurrentTime = this.isDisabledDatetime(normalizedDatetime);
      const disabled = slotState === 'blocked' || disabledByCurrentTime;

      return {
        date: datetime,
        datetime: normalizedDatetime,
        value: normalizedDatetime,
        label: this.formatTimeLabel(datetime),
        slot_state: slotState,
        disabled,
        tooltip,
        warnings,
        is_violating_restrictions: isViolatingRestrictions,
        is_overridable: isOverridable,
        violated_restrictions: violations,
        assignee_id: raw.assignee_id,
        assignee_name: raw.assignee_name,
        lead_count: raw.lead_count,
        disabled_by_current_datetime: disabledByCurrentTime,
      };
    },
    deriveDateSlotState(slot, timeSlots) {
      const explicit = slot.slot_state || slot.slotState;
      if (explicit) {
        return explicit;
      }

      const isAvailable = this.firstDefined([slot.is_available, slot.isAvailable], null);
      if (isAvailable === false) {
        return 'blocked';
      }

      if (!Array.isArray(timeSlots) || timeSlots.length === 0) {
        return 'available';
      }

      const states = timeSlots.map((s) => s.slot_state || (s.disabled ? 'blocked' : 'available'));
      if (states.some((state) => state === 'available')) {
        return 'available';
      }
      if (states.some((state) => state === 'overridable')) {
        return 'overridable';
      }
      return 'blocked';
    },
    deriveSlotState({
      violations,
      isViolatingRestrictions,
      isOverridable,
      isAvailable,
    }) {
      if (isAvailable === false) {
        return 'blocked';
      }

      if (isViolatingRestrictions === true || isViolatingRestrictions === 1) {
        if (isOverridable === false || isOverridable === 0) {
          return 'blocked';
        }
        return 'overridable';
      }

      if (isViolatingRestrictions === false || isViolatingRestrictions === 0) {
        return 'available';
      }

      if (!Array.isArray(violations) || violations.length === 0) {
        return 'available';
      }

      const hasNonOverridable = violations.some(
        (v) => this.getRestrictionValue(v, 'can_override') === false || this.getRestrictionValue(v, 'can_override') === 0,
      );

      return hasNonOverridable ? 'blocked' : 'overridable';
    },
    buildTooltip(violations, raw) {
      const lines = [];

      if (Array.isArray(raw.tooltip)) {
        lines.push(...raw.tooltip);
      } else if (typeof raw.tooltip === 'string') {
        lines.push(...raw.tooltip.split(/\n|;/g));
      }

      if (Array.isArray(violations) && violations.length) {
        lines.push(...violations.map((v) => this.getRestrictionMessage(v)));
      }

      return this.uniqueLines(lines);
    },
    buildOverridableWarnings(violations, raw) {
      const lines = [];

      if (Array.isArray(raw.warnings)) {
        lines.push(...raw.warnings);
      } else if (typeof raw.warning === 'string') {
        lines.push(...raw.warning.split(/\n|;/g));
      }

      if (Array.isArray(violations) && violations.length) {
        lines.push(
          ...violations
            .filter((v) => this.getRestrictionValue(v, 'can_override') === true || this.getRestrictionValue(v, 'can_override') === 1)
            .map((v) => this.getRestrictionMessage(v)),
        );
      }

      return this.uniqueLines(lines);
    },
    uniqueLines(lines) {
      return Array.from(new Set(
        lines
          .map((line) => String(line || '').trim())
          .filter(Boolean),
      ));
    },
    firstDefined(values, fallback = null) {
      for (let i = 0; i < values.length; i += 1) {
        if (values[i] !== undefined && values[i] !== null) {
          return values[i];
        }
      }
      return fallback;
    },
    getRestrictionValue(restriction, key) {
      if (!restriction || typeof restriction !== 'object') {
        return null;
      }
      if (Object.prototype.hasOwnProperty.call(restriction, key)) {
        return restriction[key];
      }
      const camelKey = key.replace(/_([a-z])/g, (_unused, letter) => letter.toUpperCase());
      if (Object.prototype.hasOwnProperty.call(restriction, camelKey)) {
        return restriction[camelKey];
      }
      return null;
    },
    getRestrictionMessage(restriction) {
      if (!restriction || typeof restriction !== 'object') {
        return null;
      }
      return restriction.message || restriction.constraint_message || null;
    },
    normalizeDatetime(datetime) {
      try {
        return this.$momentUtil(datetime, this.timezone).toServerFormat();
      } catch (error) {
        return String(datetime || '').substring(0, 19).replace('T', ' ');
      }
    },
    formatTimeLabel(datetime) {
      const raw = String(datetime || '').trim();
      if (/^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}/.test(raw)) {
        return this.$momentUtil.changeTz(raw.substring(0, 19).replace('T', ' '), this.timezone).toDisplayShortTimeFormat();
      }
      return this.$momentUtil(datetime, this.timezone).toDisplayShortTimeFormat();
    },
    isDisabledDatetime(datetime) {
      if (!this.disabledDatetime || !datetime) {
        return false;
      }

      try {
        return this.$momentUtil(datetime, null).isSame(this.$momentUtil(this.disabledDatetime, null));
      } catch (error) {
        return String(datetime) === String(this.disabledDatetime);
      }
    },
    isCurrentTourSlot(slot) {
      if (!this.disabledDatetime || !slot) {
        return false;
      }
      const slotRaw = String(slot.datetime || slot.date || '').substring(0, 19).replace('T', ' ');
      const target = String(this.disabledDatetime).substring(0, 19).replace('T', ' ');
      return slotRaw === target;
    },
    decorateDateOption(slot) {
      const dateMoment = this.$momentUtil.tz(slot.date, 'YYYY-MM-DD', this.timezone);
      const dayOfMonthOrdinal = dateMoment.format('Do');
      const dayOfMonthOrdinalLength = dayOfMonthOrdinal.length;

      return {
        ...slot,
        month: dateMoment.format('MMMM'),
        dayOfMonth: dateMoment.format('D'),
        dayOfMonthOrdinal: dayOfMonthOrdinal.substring(dayOfMonthOrdinalLength - 2, dayOfMonthOrdinalLength),
        dayOfWeek: dateMoment.calendar(this.$momentUtil(null, this.timezone), {
          sameDay: '[Today]',
          nextDay: '[Tomorrow]',
          nextWeek: 'ddd',
          sameElse: 'ddd',
        }),
        disabled: false,
      };
    },
    decorateTimeOption(slot) {
      const forcedDisabled = this.isCurrentTourSlot(slot);
      return {
        time: slot.label || this.formatTimeLabel(slot.datetime || slot.date),
        datetime: slot.datetime || slot.date,
        disabled: forcedDisabled || !!slot.disabled,
        slot_state: forcedDisabled ? null : (slot.slot_state || slot.slotState || (slot.disabled ? 'blocked' : 'available')),
        warnings: Array.isArray(slot.warnings) ? slot.warnings : [],
        tooltip: Array.isArray(slot.tooltip) ? slot.tooltip : [],
        violated_restrictions: Array.isArray(slot.violated_restrictions) ? slot.violated_restrictions : [],
        is_violating_restrictions: this.firstDefined([slot.is_violating_restrictions, slot.isViolatingRestrictions], null),
        is_overridable: this.firstDefined([slot.is_overridable, slot.isOverridable], null),
        assignee_id: slot.assignee_id,
        assignee_name: slot.assignee_name,
        lead_count: slot.lead_count,
      };
    },
    filterAvailableTimeSlots(timeSlots) {
      return timeSlots.filter((slot) => {
        const state = slot.slot_state || slot.slotState || (slot.disabled ? 'blocked' : 'available');
        return !slot.disabled && state === 'available';
      });
    },
    syncShowAllSlotsFromTimeSlotData() {
      const timeSlots = get(this.chosenDateOption, 'time_slots', null);
      if (!Array.isArray(timeSlots)) {
        this.showAllSlots = false;
        return;
      }
      const available = this.filterAvailableTimeSlots(timeSlots);
      if (available.length > 0) {
        this.showAllSlots = false;
        return;
      }
      this.showAllSlots = timeSlots.length > 0;
    },
    async chooseDate(dateOption) {
      if (dateOption.disabled) {
        return;
      }

      if (this.chosenDate === dateOption.date) {
        this.chosenDate = null;
        this.isLoadingDateSlots = false;
        this.clearSelectionAndEmit();
        return;
      }

      this.chosenDate = dateOption.date;
      this.clearSelectionAndEmit();

      this.isLoadingDateSlots = true;
      await this.loadDateSlots(dateOption.date);
    },
    async loadDateSlots(date) {
      try {
        const response = await this.api().request('GET', this.buildShowingSlotsUrl(date));
        const slotCollection = Array.isArray(response[this.slotsKey]) ? response[this.slotsKey] : [];
        const fetchedDay = slotCollection.find((slot) => slot.date === date);
        const mapped = fetchedDay ? this.mapShowingSlot(fetchedDay) : {
          date,
          label: this.$momentUtil(date, null).toDateFormat(),
          is_available: true,
          slot_state: 'available',
          time_slots_loaded: true,
          time_slots: [],
        };
        const idx = this.activeSlots.findIndex((slot) => slot.date === date);
        if (idx !== -1) {
          this.$set(this.showingSlots[this.slotsKey], idx, mapped);
        }
      } catch (error) {
        const idx = this.activeSlots.findIndex((slot) => slot.date === date);
        if (idx !== -1) {
          const fallback = {
            ...this.activeSlots[idx],
            time_slots_loaded: true,
            time_slots: [],
            slot_state: 'blocked',
          };
          this.$set(this.showingSlots[this.slotsKey], idx, fallback);
        }
        this.uiNotify(this.getFirstError(error) || error.message || 'Failed to load date slots', 'negative');
      } finally {
        if (this.chosenDate === date) {
          this.isLoadingDateSlots = false;
          this.syncShowAllSlotsFromTimeSlotData();
        }
      }
    },
    chooseTime(timeOption) {
      if (timeOption.disabled) {
        return;
      }

      if (this.chosenTimeValue === timeOption.datetime) {
        this.clearSelectionAndEmit();
        return;
      }

      this.chosenTimeValue = timeOption.datetime;
      this.selectedTimeOptionRef = timeOption;

      this.warningsRef = this.uniqueLines(timeOption.warnings || []);
      const isViolating = timeOption.is_violating_restrictions === true || timeOption.is_violating_restrictions === 1;
      const isOverridable = timeOption.is_overridable === true || timeOption.is_overridable === 1;

      this.restrictionWarningsRef = (timeOption.slot_state === 'overridable' || (isViolating && isOverridable))
        ? this.uniqueLines(timeOption.tooltip || [])
        : [];
      this.assigneeMismatchWarningDataRef = this.buildAssigneeReassignmentWarningData(timeOption);

      this.emitSelectedTime(timeOption);
    },
    toUtcServerFormat(datetime) {
      if (!datetime) {
        return null;
      }
      try {
        return this.$momentUtil.tz(datetime, 'YYYY-MM-DD HH:mm:ss', this.timezone).utc().format('YYYY-MM-DD HH:mm:ss');
      } catch (e) {
        return datetime;
      }
    },
    emitSelectedTime(selected) {
      if (!selected) {
        this.$emit('time-selected', { is_video: this.isVideo, time: null });
        return;
      }

      this.$emit('time-selected', {
        is_video: this.isVideo,
        time: this.toUtcServerFormat(selected.datetime),
        slot_state: selected.slot_state || null,
        warnings: selected.warnings || [],
        violated_restrictions: selected.violated_restrictions || [],
        tooltip: selected.tooltip || [],
        is_violating_restrictions: this.firstDefined([selected.is_violating_restrictions], null),
        is_overridable: this.firstDefined([selected.is_overridable], null),
        assignee_id: this.firstDefined([selected.assignee_id], null),
        assignee_name: this.firstDefined([selected.assignee_name], null),
        lead_count: this.firstDefined([selected.lead_count], null),
      });
    },
    findSelectedTimeOption(value) {
      if (!value || !this.chosenDateOption || !Array.isArray(this.chosenDateOption.time_slots)) {
        return null;
      }
      const timeSlot = this.chosenDateOption.time_slots.find((slot) => slot.value === value || slot.datetime === value);
      return timeSlot ? this.decorateTimeOption(timeSlot) : null;
    },
    buildAssigneeReassignmentWarningData(slot) {
      const slotAssigneeId = slot && slot.assignee_id;
      if (slotAssigneeId == null || this.assigneeId == null) {
        return null;
      }
      if (String(slotAssigneeId) === String(this.assigneeId)) {
        return null;
      }
      return {
        leadCount: Number(this.firstDefined([slot && slot.lead_count], 0)),
        slotAssigneeName: String((slot && slot.assignee_name) || 'another team member').trim(),
        chosenAssigneeLabel: String(this.chosenAssigneeLabel || '').trim() || 'the selected host',
      };
    },
    rebuildCurrentSlots() {
      ['slots', 'video_slots'].forEach((key) => {
        this.showingSlots[key] = this.showingSlots[key].map((slot) => {
          if (!Array.isArray(slot.time_slots)) {
            return slot;
          }
          const time_slots = slot.time_slots.map((timeSlot) => {
            const disabledByCurrentTime = this.isDisabledDatetime(timeSlot.value);
            return {
              ...timeSlot,
              disabled_by_current_datetime: disabledByCurrentTime,
              disabled: timeSlot.slot_state === 'blocked' || disabledByCurrentTime,
            };
          });

          return {
            ...slot,
            time_slots,
            slot_state: this.deriveDateSlotState(slot, time_slots),
          };
        });
      });
    },
    clearSelectionAndEmit() {
      this.chosenTimeValue = null;
      this.selectedTimeOptionRef = null;
      this.warningsRef = [];
      this.restrictionWarningsRef = [];
      this.assigneeMismatchWarningDataRef = null;
      this.emitSelectedTime(null);
    },
    resetPicker() {
      this.chosenDate = null;
      this.showAllSlots = false;
      this.isLoadingDateSlots = false;
      this.clearSelectionAndEmit();
    },
  },
};
</script>

<style scoped>
.slot-section-header {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.slot-section-label {
  font-size: 14px;
  font-weight: 500;
}

.slot-section-chosen {
  background: #027be3;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
}

.slot-card {
  background: #fff;
  border: 1px solid #d3d9e3;
  border-radius: 8px;
  color: #1f2937;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 72px;
  padding: 6px 8px;
  text-align: center;
  transition: border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease;
  width: 100%;
}

.slot-card:hover:not(:disabled) {
  border-color: #027be3;
}

.slot-card.chosen {
  background: #027be3;
  border-color: #027be3;
  color: #fff;
}

.slot-card.overridable:not(.chosen):not(:disabled) {
  background: rgba(255, 193, 7, 0.14);
  border-color: rgba(255, 193, 7, 0.75);
}

.slot-card.blocked:not(.chosen) {
  background: rgba(244, 67, 54, 0.1);
  border-color: rgba(244, 67, 54, 0.55);
  color: #c62828;
}

.slot-card:disabled {
  cursor: not-allowed;
  opacity: 1;
}

.slot-card.blocked:disabled:not(.chosen) {
  color: #c62828;
}

.date-option .month {
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
}

.date-option .day-row {
  align-items: baseline;
  display: flex;
  justify-content: center;
  line-height: 1;
}

.date-option .day-of-month {
  font-size: 30px;
  font-weight: 700;
}

.date-option .day-of-month-ordinal {
  font-size: 14px;
  font-weight: 700;
  margin-left: 1px;
}

.date-option .day-of-week {
  font-size: 14px;
  font-weight: 600;
  margin-top: 2px;
}

.slot-grid-time {
  max-height: 300px;
  overflow-y: auto;
}

.time-option {
  min-height: 58px;
  position: relative;
}

.time-option .time {
  align-items: center;
  display: inline-flex;
  font-size: 16px;
  font-weight: 700;
  justify-content: center;
}

.time-option .tooltip-indicator {
  align-items: center;
  display: inline-flex;
  margin-left: 4px;
  opacity: 0.85;
}

.bj-unit-schedule-tour ul {
  margin: 0;
}
</style>
